'use client';
import React, { useState, useMemo } from 'react';
import { usePageTranslations } from '@/context/PageTranslationsProvider';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { usePageData } from '@/context/PageDataProvider';
import { TypePlayKitScheduleFields } from '@/core/generated-types';
import {
  LayoutActiveSubscription,
  LayoutAgedOutSubscription,
  LayoutCancelledSubscription,
  LayoutNoSubscription,
  SharedLayout,
} from './components';
import {
  isSubscriptionCancelled,
  isSubscriptionFinished,
} from '@/lib/api/subscriptionManager/helpers';
import { useSearchParams } from 'next/navigation';
import { TypePlayKitsPageData } from '@/app/[locale]/(shop)/account/subscriptions/fetchPageData';

function PlayKitSchedule({
  childrenIcons,
  noticeBanner,
  holidayBanner,
  prepayBanner,
}: TypePlayKitScheduleFields) {
  const t = usePageTranslations() as TypePlayKitPageTranslations;
  const data = usePageData() as TypePlayKitsPageData;
  const { user, subscriptions } = data || {};
  const searchParams = useSearchParams();
  const defaultSelectedChildUuid = determineDefaultSelectedChildUuid();
  const [selectedChildUuid, setSelectedChildUuid] = useState<string>(
    defaultSelectedChildUuid,
  );

  const selectedSubscription = useMemo(
    () =>
      subscriptions.find(
        (subscription) => subscription.child_uuid === selectedChildUuid,
      ),
    [subscriptions, selectedChildUuid],
  );

  const selectedChild = useMemo(
    () =>
      user.visibleChildren.find((child) => child.uuid === selectedChildUuid),
    [user.visibleChildren, selectedChildUuid],
  );

  function determineDefaultSelectedChildUuid() {
    const defaultSelectedSubscription = subscriptions.find(
      (subscription) => subscription.isDefaultSelectedSubscription,
    );
    const updatedChildUuid = searchParams?.get('updated_child_uuid');
    const [firstVisibleChild] = user.visibleChildren;

    return (
      updatedChildUuid ||
      defaultSelectedSubscription?.child_uuid ||
      firstVisibleChild?.uuid
    );
  }

  let content;

  if (!selectedSubscription || !selectedChild) {
    content = <LayoutNoSubscription t={t} />;
  } else if (isSubscriptionFinished(selectedSubscription)) {
    content = (
      <LayoutAgedOutSubscription
        t={t}
        selectedSubscription={selectedSubscription}
      />
    );
  } else if (isSubscriptionCancelled(selectedSubscription)) {
    content = (
      <LayoutCancelledSubscription
        t={t}
        userUuid={selectedSubscription.recipient_uuid}
        subscriptionUuid={selectedSubscription.uuid}
      />
    );
  } else {
    content = (
      <LayoutActiveSubscription
        t={t}
        selectedChild={selectedChild}
        selectedSubscription={selectedSubscription}
        user={user}
        noticeBanner={noticeBanner}
        holidayBanner={holidayBanner}
        prepayBanner={prepayBanner}
      />
    );
  }

  return (
    <SharedLayout
      selectedChild={selectedChild}
      setSelectedChild={setSelectedChildUuid}
      t={t}
      visibleChildren={data.user.visibleChildren}
      icons={childrenIcons}
    >
      {content}
    </SharedLayout>
  );
}

export default PlayKitSchedule;
